/**
 * 자막 스트림 리듀서 — §8 이벤트 모델의 클라이언트측 구현.
 *
 * 불변식(테스트로 못박음):
 *  1. seq 는 방 전역 단조 축 — 이미 본 seq 는 무시(멱등 재생, §8.1).
 *  2. partial 은 utt 별 최신 1건만 유효(supersede) — **리스트 재정렬 금지**(§14).
 *     화면 요동 방지를 위해 partial 은 자기 자리에서 in-place 교체되고,
 *     같은 utt 의 final 이 도착하면 그 자리를 그대로 승계한다.
 *  3. retract 는 partial 만 회수한다(§6.4) — 확정된 final 은 건드리지 않는다.
 *  4. delete 는 tombstone 으로 남는다(§8.4) — 재작성 금지·재생에서도 재적용.
 *  5. gap 은 반드시 보이게 렌더된다 — "조용한 유실 금지"(§1 원칙 3).
 */

import type {
  AssistData,
  CaptionFinalData,
  CaptionGapData,
  ChatData,
  CoachData,
  Envelope,
  HistorySeedData,
  RecordParagraphData,
  RoomStatusData,
  UpstreamNotice,
} from './events'

/* ── 화면 항목 모델 ──────────────────────────────────────────────────────── */

export interface FinalItem {
  kind: 'final'
  /** 리스트 키 — utt 가 안정 식별자다(partial→final 승계에 유지). */
  id: string
  utt: string
  seq: number
  ts: number
  data: CaptionFinalData
  /** §8.4 삭제됨 — 자리는 유지하되 내용을 숨긴다. */
  deleted: boolean
  /**
   * 장문 문단(record.paragraph)에 통합됨 — 값은 흡수한 문단 항목의 id.
   * **데이터는 그대로 남는다**: 문단 카드의 `원본 N건` 아코디언과 회의록
   * 저장(부록)이 이 카드를 계속 읽는다. 최상위 피드에서만 숨겨진다.
   */
  consolidatedInto?: string
  /** 이전 회의 이어하기(S23) — true 면 이전 회의에서 시딩된 항목(read-only, 문단 흡수 미참여). */
  prev?: boolean
}

/**
 * 장문 기록 문단(record.paragraph) — 라이브 카드들이 "숙성"돼 접힌 자리.
 *
 * `seq` 는 **정렬용 좌표**다: 문단 자신의 발행 seq(=paraSeq)가 아니라
 * 덮은 카드들 중 최신 final 의 seq 를 쓴다. 문단은 발행 시점(수 분 뒤)이
 * 아니라 **그 발화들이 있던 자리**에 서야 하기 때문이다 — 자기 seq 로
 * 꽂으면 라이브 꼬리 위(최신)에 문단이 떠서 타임라인이 거짓이 된다.
 */
export interface ParagraphItem {
  kind: 'paragraph'
  id: string                 // `p-${paraSeq}` — 안정 식별자
  utt: null
  /** 정렬 좌표 — 덮은 final 중 최대 seq(없으면 발행 seq) */
  seq: number
  /** 발행 seq(중복 제거·lastSeq 축) — withSeq 가 envelope 로 처리한다 */
  paraSeq: number
  ts: number
  data: RecordParagraphData
  /** 이전 회의 이어하기(S23) — true 면 이전 회의에서 시딩된 항목(read-only, 문단 흡수 미참여). */
  prev?: boolean
}

/**
 * 진행 중인 발화 한 건 — **하나의 카드 정체성**으로 전 구간을 덮는다.
 *
 * `듣는중 → 통역 중 → partial → final` 이 모두 같은 자리에서 일어난다.
 * 별도 카드를 만들었다 지우면 목록이 튀고(§14 화면 요동 금지), 사용자는
 * "방금 그게 어디 갔지" 를 겪는다. phase 만 바뀌고 자리는 고정이다.
 */
export type PartialPhase =
  /** VAD 가 발화를 잡았다 — 아직 아무 텍스트도 없다 */
  | 'listening'
  /** 발화가 닫히고 모델 추론 중 — 여전히 텍스트 없음 */
  | 'interpreting'
  /** ko partial 이 흐르는 중 — 실제 자막이 보이기 시작 */
  | 'streaming'

export interface PartialItem {
  kind: 'partial'
  id: string
  utt: string
  /** partial 은 seq 미부여(§8.2) */
  seq: null
  ts: number
  ko: string
  phase: PartialPhase
  /** 원발화 언어 코드 — "VI 듣는중" 칩에 쓴다(헤더 칩과 같은 표기) */
  src: string
  /**
   * 서버가 실측한 진행 관측치(ms · utt.state dur_ms). listening 은 지금까지
   * 잡힌 오디오, interpreting 은 통역에 들어간 분량. **없으면 그리지 않는다**
   * — 클라이언트가 시간을 지어내면 입력이 죽어도 숫자가 굴러가는 거짓
   * 화면이 된다(레벨 미터 실사고와 같은 부류).
   */
  durMs: number | null
  /** 서버 실측 음량(dBFS). 없으면 파형을 그리지 않는다. */
  levelDbfs?: number | null
}

export interface GapItem {
  kind: 'gap'
  id: string
  utt: null
  seq: number
  ts: number
  data: CaptionGapData
}

export type FeedItem = FinalItem | PartialItem | GapItem | ParagraphItem

/** §8.5 채팅 1건 — 자막과 같은 seq 축을 쓰므로 정렬·중복 제거가 공짜다. */
export interface ChatItem {
  id: string
  seq: number
  ts: number
  data: ChatData
  /** §8.5 삭제됨 — 자리는 유지하되 내용을 숨긴다(자막 tombstone 과 같은 규율) */
  deleted: boolean
}

export interface CoachItem {
  id: string
  seq: number
  ts: number
  data: CoachData
}

/**
 * 어시스트 대화 한 항목(DEF-071 1단계).
 *
 * 질문과 답을 **한 항목**으로 묶는다: 서버가 `q` 를 에코해 주므로 짝을
 * 맞추는 데 로컬 상관 상태가 필요 없다(재접속·다른 탭도 같은 대화를 본다).
 */
export interface AssistItem {
  id: string
  seq: number
  ts: number
  data: AssistData
}

/** §7.3 적체 단계에서 파생되는 UI 상태. */
export interface RoomView {
  state: RoomStatusData['state']
  viewers: number
  lagMs: number
  level: 0 | 1 | 2 | 3
  p50Ms: number | null
  p95Ms: number | null
  /** 게이트웨이 안내(host 전용, TASK_agw_client §3) — 없으면 null. */
  upstreamNotice: UpstreamNotice | null
}

export interface StreamState {
  /** 화면 순서대로의 피드(오래된 것 → 최신). */
  items: FeedItem[]
  /** 코치 피드(호스트 전용, §8.2 vis=host). */
  coach: CoachItem[]
  /** 어시스트 대화(호스트 전용, 오래된 것 → 최신 — 대화는 위에서 아래로). */
  assist: AssistItem[]
  /** 지금까지 본 최대 seq — 재접속 ?since= 커서(§8.3). */
  lastSeq: number
  /** 멱등 재생을 위한 관측 seq 집합. */
  seen: ReadonlySet<number>
  room: RoomView
  /** on-demand 요약/의도 해석(§8.2 summary). */
  summary: { body: string; kind: 'summary' | 'intent'; ts: number } | null
  /** §8.5 회의 중 채팅(오래된 것 → 최신). 자막과 달리 **시간순**이다 —
   *  대화는 위에서 아래로 읽는 것이 자연스럽다(자막의 최신 우선과 다르다). */
  chat: ChatItem[]
  /** §7.4 caption.error 로 표면화된 마지막 오류. */
  lastError: { reason: string; ts: number } | null
  /**
   * §8.5 채팅 거부 사유 — **대화 입력 옆에 인라인으로** 보여 줄 문장.
   * 방 배너(`lastError`)와 섞지 않는다: 이건 내 입력이 거부된 사실이고
   * 고칠 사람도 나뿐이다. 방 전체 오류로 그리면 남의 문제처럼 읽힌다.
   * `ts` 는 같은 문구가 다시 와도 새 알림으로 보이게 하는 축이다.
   */
  chatError: { message: string; ts: number } | null
  /** §8.3 resync 지시로 유실이 확인된 구간이 있었는지. */
  resyncCount: number
  /**
   * §14 지금 잡히고 있는 발화 — 호스트 화면의 "듣는중/통역 중" 칩.
   * 서버 VAD 가 알려 준다(로컬 마이크 레벨로 추측하지 않는다).
   */
  live: { utt: string; state: 'open' | 'inflight'; src: string } | null
}

export const initialRoomView: RoomView = {
  state: 'open',
  viewers: 0,
  lagMs: 0,
  level: 0,
  p50Ms: null,
  p95Ms: null,
  upstreamNotice: null,
}

export function createInitialState(): StreamState {
  return {
    items: [],
    coach: [],
    assist: [],
    chat: [],
    lastSeq: 0,
    seen: new Set<number>(),
    room: initialRoomView,
    summary: null,
    lastError: null,
    chatError: null,
    resyncCount: 0,
    live: null,
  }
}

/* ── 적체 단계 판정 (§7.3) ───────────────────────────────────────────────── */

/**
 * lag(ms) → 적체 단계. 서버가 level 을 실어주면 그것을 신뢰하고,
 * 없으면 §16 기본 문턱(3/5/10s)으로 파생한다.
 */
export function lagLevel(lagMs: number): 0 | 1 | 2 | 3 {
  if (lagMs > 10_000) return 3
  if (lagMs > 5_000) return 2
  if (lagMs > 3_000) return 1
  return 0
}

/* ── 리듀서 ─────────────────────────────────────────────────────────────── */

/**
 * §7 발화 예산(12s)에 여유를 더한 안전망. 이 시간이 지나도록 final·retract 가
 * 오지 않은 partial 은 **버려진 발화**로 본다(적체 드롭·병합·연결 단절).
 * 남겨 두면 "통역 중" 카드가 영원히 떠 있고, 사용자는 오지 않을 자막을
 * 기다린다 — 조용한 유실이다. 유실로 표시하는 편이 정직하다.
 */
export const PARTIAL_STALE_MS = 15_000

export type StreamAction =
  | { type: 'event'; envelope: Envelope }
  /** 주기 틱 — 오래된 partial 을 정리한다(§1 원칙 3 "조용한 유실 금지") */
  | { type: 'tick'; now: number }
  /** §8.3 히스토리 페이지네이션으로 과거분을 앞에 덧붙인다. */
  | { type: 'prepend-history'; envelopes: Envelope[] }
  /** 재접속 시 로컬 상태를 비우고 재재생을 받는다. */
  | { type: 'reset' }

export function streamReducer(state: StreamState, action: StreamAction): StreamState {
  switch (action.type) {
    case 'reset':
      return createInitialState()
    case 'tick':
      return expireStalePartials(state, action.now)
    case 'prepend-history':
      return prependHistory(state, action.envelopes)
    case 'event':
      return applyEvent(state, action.envelope)
  }
}

function applyEvent(state: StreamState, e: Envelope): StreamState {
  // ── 1. seq 기반 중복 제거 — partial 은 seq 미부여라 이 관문을 우회한다.
  if (e.seq !== null) {
    if (state.seen.has(e.seq)) return state
  }

  switch (e.type) {
    case 'caption.partial':
      return applyPartial(state, e as Envelope<'caption.partial'>)
    case 'caption.retract':
      return applyRetract(state, e as Envelope<'caption.retract'>)
    case 'caption.final':
      return applyFinal(state, e as Envelope<'caption.final'>)
    case 'caption.gap':
      return applyGap(state, e as Envelope<'caption.gap'>)
    case 'caption.delete':
      return applyDelete(state, e as Envelope<'caption.delete'>)
    case 'caption.error': {
      const reason = (e as Envelope<'caption.error'>).data.reason
      if (reason === 'audio_resumed') {
        return withSeq(state, e, { lastError: null })
      }
      return withSeq(state, e, { lastError: { reason, ts: e.ts } })
    }
    case 'coach':
      return applyCoach(state, e as Envelope<'coach'>)
    case 'assist':
      return applyAssist(state, e as Envelope<'assist'>)
    case 'chat':
      return applyChat(state, e as Envelope<'chat'>)
    case 'chat.delete':
      return applyChatDelete(state, e as Envelope<'chat.delete'>)
    case 'chat.error':
      // 서버 문구를 **그대로** 싣는다 — 거부 사유의 정본은 서버다. 화면이
      // 자기 문장을 지어내면 두 곳이 다른 얘기를 하게 된다.
      return withSeq(state, e, {
        chatError: {
          message: (e as Envelope<'chat.error'>).data.message,
          ts: e.ts,
        },
      })
    case 'summary': {
      const d = (e as Envelope<'summary'>).data
      // 서버는 요약/의도를 구분해 싣지 않는다 — 없으면 '요약'(§8.2).
      return withSeq(state, e, {
        summary: { body: d.body, kind: d.kind ?? 'summary', ts: e.ts },
      })
    }
    case 'room.status':
      return applyRoomStatus(state, e as Envelope<'room.status'>)
    case 'utt.state':
      return applyUttState(state, e as Envelope<'utt.state'>)
    case 'speaker.turn':
      return applySpeakerTurn(state, e as Envelope<'speaker.turn'>)
    case 'caption.review':
      return applyCaptionReview(state, e as Envelope<'caption.review'>)
    case 'record.paragraph':
      return applyParagraph(state, e as Envelope<'record.paragraph'>)
    case 'history.seed':
      return applyHistorySeed(state, e as Envelope<'history.seed'>)
    case 'resync':
      return applyResync(state, e as Envelope<'resync'>)
    // caption.skip 은 호스트 참고 표시 — 피드에 항목을 만들지 않는다(§8.2).
    case 'caption.skip':
      return withSeq(state, e, {})
    default:
      return state
  }
}

/** seq 를 관측 집합·커서에 반영하며 부분 갱신을 합성한다. */
function withSeq(
  state: StreamState,
  e: Envelope,
  patch: Partial<StreamState>,
): StreamState {
  if (e.seq === null) return { ...state, ...patch }
  const seen = new Set(state.seen)
  seen.add(e.seq)
  return {
    ...state,
    ...patch,
    seen,
    lastSeq: Math.max(state.lastSeq, e.seq),
  }
}

function applyPartial(state: StreamState, e: Envelope<'caption.partial'>): StreamState {
  const { utt, ko } = e.data
  const idx = state.items.findIndex((it) => it.utt === utt)

  if (idx >= 0) {
    const existing = state.items[idx]
    // 같은 utt 의 final 이 이미 도착했으면 뒤늦은 partial 은 무시한다
    // (final 은 불변 — §8.1).
    if (!existing || existing.kind !== 'partial') return state
    // supersede: 자기 자리에서 in-place 교체 — 재정렬 없음(§14).
    // 자리표(듣는중/통역 중)가 여기서 **실제 자막으로 변한다**.
    const items = state.items.slice()
    items[idx] = { ...existing, ko, ts: e.ts, phase: 'streaming' }
    return { ...state, items }
  }

  // 새 utt — 항상 **맨 위**(최신)에 붙는다(§14 최신 우선 정렬).
  // utt.state 를 못 받았거나(뷰어) partial 이 먼저 온 경우 — 바로 streaming.
  const item: PartialItem = {
    kind: 'partial', id: utt, utt, seq: null, ts: e.ts, ko,
    phase: 'streaming', src: state.live?.src ?? '', durMs: null,
    levelDbfs: null,
  }
  return { ...state, items: [item, ...state.items] }
}

function applyRetract(state: StreamState, e: Envelope<'caption.retract'>): StreamState {
  const { utt } = e.data
  // partial 만 회수한다 — final 은 불변(§6.4).
  const items = state.items.filter((it) => !(it.kind === 'partial' && it.utt === utt))
  const next = items.length === state.items.length ? state.items : items
  return withSeq(state, e, { items: next })
}

function applyFinal(state: StreamState, e: Envelope<'caption.final'>): StreamState {
  const utt = e.utt ?? e.data.ts_utt.toString()
  const seq = e.seq
  if (seq === null) return state

  const item: FinalItem = {
    kind: 'final',
    id: utt,
    utt,
    seq,
    ts: e.ts,
    data: e.data,
    deleted: false,
  }

  const idx = state.items.findIndex((it) => it.utt === utt)
  let items: FeedItem[]
  if (idx >= 0) {
    const existing = state.items[idx]
    // 같은 utt 의 final 재도착 = 멱등(§8.1) — seen 관문을 뚫고 온 경우만 여기 온다.
    if (existing?.kind === 'final') return withSeq(state, e, {})
    // partial 이 있던 자리를 그대로 승계한다 — 재정렬 없음(§14).
    items = state.items.slice()
    items[idx] = item
  } else {
    items = insertBySeq(state.items, item)
  }
  // 자막이 도착했다는 것 자체가 오디오가 흐르고 있다는 증거다 — 무음 경고가
  // 아직 안 지워졌으면 여기서 해제한다(클라이언트 쪽 안전망).
  const clearStale = state.lastError?.reason === 'audio_silent'
  return withSeq(state, e, clearStale ? { items, lastError: null } : { items })
}

/**
 * 인접한 같은 사유의 gap 을 **하나로 합친다**.
 *
 * 재접속이 반복되면 client_gap 이 연달아 날아와 동일한 "유실 구간" 막대가
 * 벽처럼 쌓인다(실측: 죽은 방에 재접속을 반복하다 화면이 gap 으로 덮였다).
 * 유실은 반드시 보여야 하지만(§1 원칙 3), **몇 번 끊겼는지가 아니라 어느
 * 구간을 잃었는지**가 정보다. 붙어 있는 구간은 하나의 막대로 합친다.
 */
function mergeGapInto(existing: GapItem, incoming: CaptionGapData): GapItem | null {
  if (existing.data.reason !== incoming.reason) return null
  const aFrom = existing.data.from_ts
  const aTo = existing.data.to_ts
  const bFrom = incoming.from_ts
  const bTo = incoming.to_ts
  // 시각 정보가 없으면(0) 합치지 않는다 — 구간을 지어내지 않는다.
  if (!aTo || !bTo) return null
  // 겹치거나 2초 이내로 이어지면 같은 유실 구간으로 본다.
  const contiguous = bFrom <= aTo + 2_000 && aFrom <= bTo + 2_000
  if (!contiguous) return null
  const frames =
    (existing.data.frames_lost ?? 0) + (incoming.frames_lost ?? 0) || undefined
  const from_ts = Math.min(aFrom, bFrom)
  const to_ts = Math.max(aTo, bTo)
  // D3 — 사유가 섞인 구간에 한 사유를 단정하지 않는다(없는 정보를 지어내지
  // 않는다). 둘 다 있고 다르면 undefined, 한쪽만 있으면 그 값을 보존한다
  // (DEF-100 — 서버 갭엔 client_reason 이 없다. undefined vs 값도 "다름"으로
  // 취급하면 사유가 가장 필요한 긴 단절에서 오히려 사라진다).
  const client_reason =
    existing.data.client_reason !== undefined && incoming.client_reason !== undefined
      ? existing.data.client_reason === incoming.client_reason
        ? existing.data.client_reason
        : undefined
      : (existing.data.client_reason ?? incoming.client_reason)
  return {
    ...existing,
    data: {
      ...existing.data,
      from_ts,
      to_ts,
      ...(frames === undefined ? {} : { frames_lost: frames }),
      approx_ms: Math.max(0, to_ts - from_ts),
      client_reason,
    },
  }
}

function applyGap(state: StreamState, e: Envelope<'caption.gap'>): StreamState {
  if (e.seq === null) return state

  // 맨 위(최신)가 합칠 수 있는 gap 이면 막대를 늘린다.
  const head = state.items[0]
  if (head?.kind === 'gap') {
    const merged = mergeGapInto(head, e.data)
    if (merged) {
      const items = state.items.slice()
      items[0] = merged
      return withSeq(state, e, { items })
    }
  }

  const item: GapItem = {
    kind: 'gap',
    id: `gap-${e.seq}`,
    utt: null,
    seq: e.seq,
    ts: e.ts,
    data: e.data,
  }
  return withSeq(state, e, { items: insertBySeq(state.items, item) })
}

function applyDelete(state: StreamState, e: Envelope<'caption.delete'>): StreamState {
  const { target_seq, target_utt } = e.data
  let touched = false
  const items = state.items.map((it) => {
    if (it.kind !== 'final') return it
    const hit =
      (target_utt !== null && it.utt === target_utt) ||
      (target_seq !== null && it.seq === target_seq)
    if (!hit || it.deleted) return it
    touched = true
    // tombstone — 자리는 남기고 내용을 숨긴다(§8.4 재작성 금지).
    return { ...it, deleted: true }
  })
  return withSeq(state, e, touched ? { items } : {})
}

/**
 * §8.5 채팅 — 대화는 **시간순**(오래된 것 위)으로 쌓는다.
 * 자막의 최신 우선과 방향이 다른 이유: 대화는 흐름을 따라 읽는 것이고,
 * 자막은 "지금 무슨 말인가" 를 보는 것이다. 용도가 다르면 정렬도 다르다.
 */
function applyChat(state: StreamState, e: Envelope<'chat'>): StreamState {
  if (e.seq === null) return state
  const item: ChatItem = {
    id: `chat-${e.seq}`,
    seq: e.seq,
    ts: e.ts,
    data: e.data,
    deleted: false,
  }
  // 재생·지연 로드로 순서가 뒤섞여 와도 seq 오름차순을 유지한다.
  const chat = [...state.chat, item].sort((a, b) => a.seq - b.seq)
  return withSeq(state, e, { chat })
}

/** §8.5 채팅 삭제 — 자막 tombstone(§8.4)과 같은 규율(재작성 금지). */
function applyChatDelete(
  state: StreamState,
  e: Envelope<'chat.delete'>,
): StreamState {
  const { target_seq } = e.data
  let touched = false
  const chat = state.chat.map((c) => {
    if (c.seq !== target_seq || c.deleted) return c
    touched = true
    return { ...c, deleted: true }
  })
  return withSeq(state, e, touched ? { chat } : {})
}

function applyCoach(state: StreamState, e: Envelope<'coach'>): StreamState {
  if (e.seq === null) return state
  const item: CoachItem = { id: `coach-${e.seq}`, seq: e.seq, ts: e.ts, data: e.data }
  return withSeq(state, e, { coach: [...state.coach, item] })
}

function applyAssist(state: StreamState, e: Envelope<'assist'>): StreamState {
  if (e.seq === null) return state
  const item: AssistItem = { id: `assist-${e.seq}`, seq: e.seq, ts: e.ts, data: e.data }
  return withSeq(state, e, { assist: [...state.assist, item] })
}

function applyRoomStatus(state: StreamState, e: Envelope<'room.status'>): StreamState {
  const d = e.data
  const room: RoomView = {
    state: d.state,
    viewers: d.viewers,
    lagMs: d.lag_ms,
    level: d.level ?? lagLevel(d.lag_ms),
    p50Ms: d.p50_ms ?? null,
    p95Ms: d.p95_ms ?? null,
    upstreamNotice: d.upstream_notice ?? null,
  }
  return withSeq(state, e, { room })
}

/**
 * §8.3 resync — 링버퍼 창을 넘겨 재접속했다. 로컬 피드를 버리고
 * 유실 구간을 gap 으로 명시한 뒤 재재생을 받는다("조용한 유실 금지").
 */
/**
 * §14 발화 수명주기 — "듣는중"(open) / "통역 중"(inflight) / 사라짐(done).
 *
 * done 은 **그 발화가 현재 표시 대상일 때만** 지운다. 늦게 도착한 이전
 * 발화의 done 이 새 발화의 표시를 꺼 버리면 칩이 깜빡인다.
 */
/**
 * 오지 않을 자막을 기다리는 partial 카드를 걷어낸다.
 *
 * 실측: 적체 제어로 드롭된 발화의 "통역 중 이▌" 카드가 확정 자막들 사이에
 * 남아 있었다. 서버는 retract 를 보내지만 연결이 끊겼거나 유실되면 도달하지
 * 않는다 — 화면이 스스로 시간을 재는 안전망이 필요하다.
 */
function expireStalePartials(state: StreamState, now: number): StreamState {
  const stale = state.items.filter(
    (i) => i.kind === 'partial' && now - i.ts > PARTIAL_STALE_MS,
  )
  if (stale.length === 0) return state
  const staleUtts = new Set(stale.map((i) => i.utt))
  const items = state.items.filter((i) => !(i.kind === 'partial' && staleUtts.has(i.utt)))
  // 표시 중인 발화가 만료됐다면 칩도 끈다.
  const live = state.live && staleUtts.has(state.live.utt) ? null : state.live
  return { ...state, items, live }
}

function applyUttState(state: StreamState, e: Envelope<'utt.state'>): StreamState {
  const { utt, state: us, src } = e.data
  const durMs = typeof e.data.dur_ms === 'number' ? e.data.dur_ms : null
  const lvl =
    typeof e.data.level_dbfs === 'number' ? e.data.level_dbfs : null

  if (us === 'done') {
    // 발화가 닫혔다. **텍스트를 한 글자도 못 받은** 자리표는 걷어낸다 —
    // skip·드롭·오류로 끝난 경우다. 여기서 안 지우면 "듣는중" 카드가
    // 영원히 남는다(§1 원칙 3 — 오지 않을 것을 기다리게 두지 않는다).
    // partial 이 흐른 카드는 그대로 둔다: 곧 final 이 그 자리를 승계한다.
    const items = state.items.filter(
      (it) => !(it.kind === 'partial' && it.utt === utt && it.ko === ''),
    )
    const live = state.live?.utt === utt ? null : state.live
    return { ...state, items, live }
  }

  const phase: PartialPhase = us === 'inflight' ? 'interpreting' : 'listening'
  const idx = state.items.findIndex((it) => it.utt === utt)

  if (idx >= 0) {
    const existing = state.items[idx]
    // 이미 확정된 자리는 건드리지 않는다(final 은 불변 — §8.1).
    if (!existing || existing.kind !== 'partial') {
      return { ...state, live: { utt, state: us, src } }
    }
    // 텍스트가 흐르기 시작했으면 phase 를 되돌리지 않는다
    // (streaming → interpreting 으로 후퇴하면 화면이 깜빡인다).
    if (existing.phase === 'streaming') {
      return { ...state, live: { utt, state: us, src } }
    }
    const items = state.items.slice()
    // 진행 관측치 갱신 — 서버 재방출(1s 심박)이 ts 도 함께 밀어 주므로
    // stale 안전망(15s)이 20s 강제 절단 발화의 카드를 도중에 걷지 않는다.
    items[idx] = {
      ...existing, phase, src, ts: e.ts,
      durMs: durMs ?? existing.durMs,
      levelDbfs: lvl ?? existing.levelDbfs ?? null,
    }
    return { ...state, items, live: { utt, state: us, src } }
  }

  // 새 발화 — 맨 위에 자리표를 세운다(§14 최신 우선).
  const item: PartialItem = {
    kind: 'partial',
    id: utt,
    utt,
    seq: null,
    ts: e.ts,
    ko: '',
    phase,
    src,
    durMs,
    levelDbfs: lvl,
  }
  return { ...state, items: [item, ...state.items], live: { utt, state: us, src } }
}

/**
 * 화자 전환 표시 소급 — 자막은 표시 없이 먼저 왔고, 서버 임베딩이 끝난 뒤 이
 * 이벤트가 **이미 그려진 카드**에 표시를 붙인다(후처리 계약의 화면측 절반).
 *
 * `changed=false`(같은 화자)도 싣는다: 화면에 구분선을 그리지 않는 것은
 * 같지만, "판정했고 안 바뀌었다" 와 "판정을 못 했다" 는 다른 사실이다.
 */
function applySpeakerTurn(
  state: StreamState,
  e: Envelope<'speaker.turn'>,
): StreamState {
  const { utt, changed } = e.data
  if (!utt || typeof changed !== 'boolean') return state
  const idx = state.items.findIndex((it) => it.kind === 'final' && it.utt === utt)
  if (idx < 0) return state
  const existing = state.items[idx]
  if (!existing || existing.kind !== 'final') return state
  if (existing.data.spk_turn === changed) return state
  const items = state.items.slice()
  items[idx] = { ...existing, data: { ...existing.data, spk_turn: changed } }
  return { ...state, items }
}

/**
 * 검토 배지 소급(DEF-019) — 자막은 배지 없이 먼저 왔고, 비동기 교차 확인이
 * 끝난 뒤 이 이벤트가 **이미 그려진 카드**에 "확인 필요" 를 붙인다.
 *
 * 사유는 **병합**한다(덮어쓰지 않는다): 게이트가 붙인 `gate_suspect` 가
 * 소급 배지 하나에 지워지면 그것이 바로 fix4 #6 이 고친 결함이다. 자막
 * 본문은 한 글자도 건드리지 않는다 — 배지는 표시 계층에만 있다.
 */
function applyCaptionReview(
  state: StreamState,
  e: Envelope<'caption.review'>,
): StreamState {
  const { utt, review_reason: code } = e.data
  if (!utt || !code) return state
  const idx = state.items.findIndex((it) => it.kind === 'final' && it.utt === utt)
  if (idx < 0) return state
  const existing = state.items[idx]
  if (!existing || existing.kind !== 'final') return state
  const prev = existing.data.review_reason ?? ''
  const codes = prev.split(',').map((c) => c.trim()).filter(Boolean)
  if (codes.includes(code)) return state
  codes.push(code)
  const items = state.items.slice()
  items[idx] = {
    ...existing,
    data: { ...existing.data, needs_review: true, review_reason: codes.join(',') },
  }
  return { ...state, items }
}

/**
 * 장문 문단 도착 — 덮인 라이브 카드들을 문단으로 **통합**한다.
 *
 * 통합은 제거가 아니다: 덮인 final 은 `consolidatedInto` 표식만 얻고 데이터
 * 그대로 남는다(문단 카드의 아코디언·회의록 부록·화자 표시 소급이 계속
 * 읽는다). 최상위 피드에서 숨기는 것은 표시 계층(caption-feed)의 일이다 —
 * 상태가 표시 정책을 선점하면 "뷰포트 밖만 접기" 같은 UX 규칙을 화면이
 * 결정할 수 없게 된다.
 */
function applyParagraph(
  state: StreamState,
  e: Envelope<'record.paragraph'>,
): StreamState {
  if (e.seq === null) return state
  const d = e.data
  const covered = new Set(d.utt_ids)
  const id = `p-${e.seq}`
  let posSeq = 0
  const items: FeedItem[] = state.items.map((it) => {
    if (it.kind !== 'final' || !covered.has(it.utt)) return it
    if (it.seq > posSeq) posSeq = it.seq
    return { ...it, consolidatedInto: id }
  })
  const item: ParagraphItem = {
    kind: 'paragraph',
    id,
    utt: null,
    // 덮은 카드가 하나도 화면에 없으면(링 창 밖 늦은 입장) 자기 seq 로
    // 선다 — 문단만이라도 타임라인의 그 자리에 남는 편이 정직하다.
    seq: posSeq || e.seq,
    paraSeq: e.seq,
    ts: d.ts || e.ts,
    data: d,
  }
  return withSeq(state, e, { items: insertBySeq(items, item) })
}

/**
 * 이전 회의 이어하기(S23) — 아카이브 행(caption/paragraph) 1건을 read-only
 * 피드 항목으로 시딩한다. `EventBus.restore` 를 통해 일반 재생 경로로 온다
 * (새 트랜스포트 없음). `prev: true` 로 표식만 남기고, 문단 흡수(covered)에
 * 걸리지 않도록 utt=''·utt_ids=[] 로 비워 둔다(§ applyParagraph 결합 방지).
 */
function applyHistorySeed(state: StreamState, e: Envelope<'history.seed'>): StreamState {
  if (e.seq === null) return state
  const d: HistorySeedData = e.data
  if (d.kind === 'caption') {
    const item: FinalItem = {
      kind: 'final',
      id: `seed-${e.seq}`,
      utt: '',
      seq: e.seq,
      ts: d.ts || e.ts,
      data: {
        lang: d.lang,
        text: d.text,
        ko: d.ko,
        conf: 1,
        flag: '',
        cut: false,
        attempt: 0,
        ts_utt: d.ts || e.ts,
      },
      deleted: false,
      prev: true,
    }
    return withSeq(state, e, { items: insertBySeq(state.items, item) })
  }
  // paragraph
  const item: ParagraphItem = {
    kind: 'paragraph',
    id: `seed-p-${e.seq}`,
    utt: null,
    seq: e.seq,
    paraSeq: e.seq,
    ts: d.ts || e.ts,
    data: {
      ts: d.ts || e.ts,
      dur_s: 0,
      lang: d.lang,
      ko: d.ko,
      utt_ids: [],
      uncertain: d.uncertain,
    },
    prev: true,
  }
  return withSeq(state, e, { items: insertBySeq(state.items, item) })
}

function applyResync(state: StreamState, e: Envelope<'resync'>): StreamState {
  const gap: GapItem = {
    kind: 'gap',
    id: `resync-${e.ts}`,
    utt: null,
    // resync 마커는 재재생분보다 오래된 위치(=아래)에 오도록 커서 직전 seq 를
    // 준다 — 최신 우선 정렬에서 낮은 seq 는 아래쪽이다.
    seq: Math.max(0, e.data.from_seq - 1),
    ts: e.ts,
    data: { from_ts: 0, to_ts: e.ts, reason: 'drop' },
  }
  return {
    ...createInitialState(),
    room: state.room,
    coach: state.coach,
    // 대화는 자막 유실 구간과 무관하다 — resync 로 지우면 호스트가 방금
    // 받은 답을 잃는다(coach 를 보존하는 것과 같은 근거).
    assist: state.assist,
    items: [gap],
    lastSeq: gap.seq,
    resyncCount: state.resyncCount + 1,
  }
}

/**
 * seq **내림차순**(최신이 위)을 지키며 삽입한다(§14 최신 우선 정렬).
 *
 * 회의실 프로젝터 용례에서 최신 자막은 스크롤 없이 보여야 한다. 대개 새 항목이
 * 맨 앞에 오므로 앞에서부터 훑는다.
 *
 * partial(seq=null)은 "아직 확정 안 된 최신"이라 **확정 항목보다 앞**에 남는다.
 */
function insertBySeq(items: readonly FeedItem[], item: FeedItem & { seq: number }): FeedItem[] {
  const out = items.slice()
  let i = 0
  while (i < out.length) {
    const cur = out[i]
    if (!cur) break
    // partial 은 항상 확정 항목보다 위에 뜬다.
    if (cur.seq === null) {
      i += 1
      continue
    }
    if (cur.seq <= item.seq) break
    i += 1
  }
  out.splice(i, 0, item)
  return out
}

/** §8.3 히스토리 페이지네이션 — 과거분을 **아래**에 잇는다(최신 우선 정렬, 중복 seq 는 버린다). */
function prependHistory(state: StreamState, envelopes: readonly Envelope[]): StreamState {
  const seen = new Set(state.seen)
  const older: FeedItem[] = []
  const olderChat: ChatItem[] = []
  const olderAssist: AssistItem[] = []
  const olderParas: Envelope<'record.paragraph'>[] = []

  for (const e of envelopes) {
    if (e.seq === null || seen.has(e.seq)) continue
    seen.add(e.seq)
    if (e.type === 'record.paragraph') {
      // 문단은 좌표(덮은 final 의 seq)가 병합 **후**에야 정해진다 — 아래서
      // 통합과 함께 꽂는다(applyParagraph 와 같은 규칙).
      olderParas.push(e as Envelope<'record.paragraph'>)
    } else if (e.type === 'caption.final') {
      const utt = e.utt ?? `h-${e.seq}`
      older.push({
        kind: 'final',
        id: utt,
        utt,
        seq: e.seq,
        ts: e.ts,
        data: (e as Envelope<'caption.final'>).data,
        deleted: false,
      })
    } else if (e.type === 'chat') {
      olderChat.push({
        id: `chat-${e.seq}`,
        seq: e.seq,
        ts: e.ts,
        data: (e as Envelope<'chat'>).data,
        deleted: false,
      })
    } else if (e.type === 'assist') {
      // DEF-073 — 대화는 **남아 있을 것으로 기대되는 산출물**이다. 자막과
      // 달리 새로고침으로 사라지면 안 된다(resync 가 assist 를 보존하는
      // 것과 같은 축). 형상은 applyAssist 와 동일해야 한다.
      olderAssist.push({
        id: `assist-${e.seq}`,
        seq: e.seq,
        ts: e.ts,
        data: (e as Envelope<'assist'>).data,
      })
    } else if (e.type === 'caption.gap') {
      older.push({
        kind: 'gap',
        id: `gap-${e.seq}`,
        utt: null,
        seq: e.seq,
        ts: e.ts,
        data: (e as Envelope<'caption.gap'>).data,
      })
    }
    // 히스토리에 섞여 온 delete 는 아래에서 tombstone 으로 재적용한다.
  }

  // 최신 우선 정렬(§14) — 과거분은 내림차순으로 정렬해 **아래**에 잇는다.
  older.sort((a, b) => (b.seq ?? 0) - (a.seq ?? 0))
  let items = [...state.items, ...older]

  // 지연 로드된 문단을 통합·삽입한다 — 이미 있던 문단(라이브로 먼저 도착)의
  // utt_ids 가 이번에 로드된 과거 final 을 덮는 경우도 여기서 붙는다.
  for (const pe of olderParas) {
    if (pe.seq === null) continue
    const covered = new Set(pe.data.utt_ids)
    const id = `p-${pe.seq}`
    let posSeq = 0
    items = items.map((it) => {
      if (it.kind !== 'final' || !covered.has(it.utt)) return it
      if (it.seq > posSeq) posSeq = it.seq
      return { ...it, consolidatedInto: id }
    })
    items = insertBySeq(items, {
      kind: 'paragraph', id, utt: null, seq: posSeq || pe.seq,
      paraSeq: pe.seq, ts: pe.data.ts || pe.ts, data: pe.data,
    })
  }
  for (const p of items) {
    if (p.kind !== 'paragraph') continue
    const covered = new Set(p.data.utt_ids)
    items = items.map((it) =>
      it.kind === 'final' && !it.consolidatedInto && covered.has(it.utt)
        ? { ...it, consolidatedInto: p.id }
        : it,
    )
  }

  // §8.4 — 삭제는 seq 를 부여받는 정규 이벤트라 재생 시에도 순서대로 적용된다.
  for (const e of envelopes) {
    if (e.type !== 'caption.delete') continue
    const { target_seq, target_utt } = (e as Envelope<'caption.delete'>).data
    items = items.map((it) =>
      it.kind === 'final' &&
      ((target_utt !== null && it.utt === target_utt) ||
        (target_seq !== null && it.seq === target_seq))
        ? { ...it, deleted: true }
        : it,
    )
  }

  // §8.5 채팅도 지연 로드분을 합쳐 시간순을 유지한다.
  let chat = state.chat
  if (olderChat.length > 0) {
    chat = [...olderChat, ...state.chat].sort((a, b) => a.seq - b.seq)
  }
  for (const e of envelopes) {
    if (e.type !== 'chat.delete') continue
    const { target_seq } = (e as Envelope<'chat.delete'>).data
    chat = chat.map((c) => (c.seq === target_seq ? { ...c, deleted: true } : c))
  }

  // DEF-073 — 대화는 위에서 아래로 읽는다(applyAssist 가 뒤에 잇는 것과
  // 같은 축). 히스토리분을 합쳐 **seq 오름차순**으로 정렬한다.
  let assist = state.assist
  if (olderAssist.length > 0) {
    assist = [...olderAssist, ...state.assist].sort((a, b) => a.seq - b.seq)
  }

  return { ...state, items, chat, assist, seen }
}
